// Э-7. Сводка смены: изменения за 72 часа, открытые задачи, проблемные рейсы, заметки предыдущей смены,
// кнопка «Принять смену». Цель — передача смены за 5–10 минут вместо 1–1,5 часа.

import { useState } from "react";
import { fmtDate, fmtDateTime, fmtNum, fmtTime } from "../../shared/format";
import { useLive } from "../../shared/live";
import { navigate } from "../../shared/router";
import { TopBar } from "../../shared/shell";
import { Button, Empty, Field, Input, Loading, Modal, Tabs, useAsync, useToast } from "../../shared/ui";
import { CloseTaskDialog, type TaskRow } from "../components/TaskClose";
import { api, can, live, ORDER_STATUS, useCtx } from "../ctx";
import { ShiftHistory } from "./ShiftHistory";

export function ShiftPage() {
  const { me } = useCtx();
  const toast = useToast();
  const s = useAsync<any>(() => api.get("/shifts/current/summary"), []);
  const [closing, setClosing] = useState(false);
  const [notes, setNotes] = useState("");
  const [newTask, setNewTask] = useState("");
  const [closingTask, setClosingTask] = useState<TaskRow | null>(null);
  // П-2 (22.10.2026): история смен — администратору, руководителю и старшему диспетчеру
  const history = ["admin", "director"].includes(me.role) || !!me.is_senior;
  const [tab, setTab] = useState<"now" | "history">("now");
  useLive(live, ["order.", "trip.", "task.", "shift."], () => s.reload(), 600);
  const d = s.data;
  const dispatcher = can.dispatch(me.role);

  const open = async () => {
    try { await api.post("/shifts/open", { kind: "dispatcher" }); toast.ok("Смена открыта"); s.reload(); } catch (e) { toast.error(e); }
  };
  const accept = async () => {
    try { await api.post(`/shifts/${d.previous_shift.id}/accept`); toast.ok("Смена принята"); s.reload(); } catch (e) { toast.error(e); }
  };

  return (
    <>
      <TopBar title="Смена и задачи">
        {d && dispatcher && !d.current_shift && d.previous_shift && !d.previous_shift.accepted_at && (
          <Button variant="primary" onClick={accept}>Принять смену</Button>
        )}
        {d && dispatcher && !d.current_shift && (!d.previous_shift || d.previous_shift.accepted_at) && <Button variant="primary" onClick={open}>Открыть смену</Button>}
        {d?.current_shift?.mine && <Button onClick={() => setClosing(true)}>Сдать смену</Button>}
      </TopBar>
      <div className="content">
        {history && <Tabs value={tab} onChange={setTab} items={[{ value: "now", label: "Текущая смена" }, { value: "history", label: "История смен" }]} />}
        {tab === "history" ? <ShiftHistory /> : !d ? <Loading error={s.error} onRetry={s.reload} /> : (
          <>
            {d.current_shift && (
              <p className="muted">Смену ведёт {d.current_shift.user} с {fmtDateTime(d.current_shift.opened_at)}.</p>
            )}
            {d.previous_shift && (
              <div className="alert info">
                <div className="t">Заметки предыдущей смены: {d.previous_shift.user}, сдана {fmtDateTime(d.previous_shift.closed_at)}</div>
                <div style={{ whiteSpace: "pre-wrap", marginTop: 4 }}>{d.previous_shift.notes || "Заметок нет."}</div>
                {d.previous_shift.accepted_by && <div className="d">Принял(а): {d.previous_shift.accepted_by}</div>}
              </div>
            )}
            <div className="cols-2">
              <Block title={`Проблемные рейсы и предупреждения: ${d.problems.length}`}>
                {d.problems.length === 0 && <Empty>Проблем нет.</Empty>}
                {d.problems.map((p: any, i: number) => (
                  <div key={i} className={`alert${p.level === "late" ? " risk" : ""}`} style={{ marginBottom: 6, cursor: p.order_id ? "pointer" : "default" }}
                    onClick={() => p.order_id && navigate("/schedule", { order: p.order_id })}>
                    <div className="t">{p.text}</div>
                  </div>
                ))}
              </Block>
              <Block title={`Открытые задачи: ${d.tasks.length}`}>
                {dispatcher && (
                  <div className="row" style={{ marginBottom: 8 }}>
                    <Input className="grow" placeholder="Перезвонить, уточнить время…" value={newTask} onChange={(e) => setNewTask(e.target.value)} />
                    <Button disabled={newTask.trim().length < 2} onClick={async () => {
                      try { await api.post("/tasks", { kind: "call_back", title: newTask }); setNewTask(""); s.reload(); } catch (e) { toast.error(e); }
                    }}>Добавить</Button>
                  </div>
                )}
                {d.tasks.length === 0 && <Empty>Открытых задач нет.</Empty>}
                {d.tasks.map((t: any) => (
                  <label key={t.id} className="check" style={{ display: "flex", padding: "6px 0", borderBottom: "1px solid var(--line-soft)" }}>
                    <input type="checkbox" checked={false} onChange={() => setClosingTask(t)} />
                    <span className="grow">{t.title}{t.overdue && <span className="tag s-conflict" style={{ marginLeft: 6 }}>просрочена</span>}</span>
                    {t.order_id && <Button size="small" variant="ghost" onClick={(e) => { e.preventDefault(); navigate("/schedule", { order: t.order_id }); }}>Заявка</Button>}
                  </label>
                ))}
              </Block>
            </div>
            <div className="cols-3">
              <Changes title="Новые заявки" rows={d.created} />
              <Changes title="Перенесённые" rows={d.rescheduled} />
              <Changes title="Отменённые" rows={d.cancelled} />
            </div>
            {d.changed.length > 0 && <Changes title="Изменены время, объём или марка" rows={d.changed} />}
            {dispatcher && <ManualShipments />}
            <p className="muted small">Изменения за последние 72 часа — после трёх выходных видно всё, что произошло.</p>
          </>
        )}
      </div>
      {closingTask && <CloseTaskDialog task={closingTask} onClose={() => setClosingTask(null)} onDone={() => { setClosingTask(null); s.reload(); }} />}
      {closing && d?.current_shift && (
        <Modal title="Сдать смену" onClose={() => setClosing(false)} actions={<>
          <Button onClick={() => setClosing(false)}>Отмена</Button>
          <Button variant="primary" onClick={async () => {
            try { await api.post(`/shifts/${d.current_shift.id}/close`, { handover_notes: notes }); toast.ok("Смена сдана"); setClosing(false); s.reload(); } catch (e) { toast.error(e); }
          }}>Сдать смену</Button>
        </>}>
          <Field label="Заметки для следующей смены" hint="Вместо стикеров на мониторе: что важно знать сменщику">
            <textarea className="input" rows={5} value={notes} onChange={(e) => setNotes(e.target.value)} />
          </Field>
        </Modal>
      )}
    </>
  );
}

function Block({ title, children }: { title: string; children: React.ReactNode }) {
  return <div className="panel"><div className="ph"><h2>{title}</h2></div><div className="pb">{children}</div></div>;
}

function Changes({ title, rows }: { title: string; rows: any[] }) {
  return (
    <Block title={`${title}: ${rows.length}`}>
      {rows.length === 0 ? <p className="muted">Нет.</p> : (
        <div className="stack tight">
          {rows.map((r, i) => (
            <div key={i} className="order-row" style={{ padding: "6px 0" }} onClick={() => navigate("/schedule", { order: r.order_id, date: r.date })}>
              <div className="t1"><span>№{r.number} {r.customer}</span><span className="num small muted">{fmtDate(r.date)}</span></div>
              <div className="small muted">{r.site}, {fmtNum(r.volume_m3)} м³, {ORDER_STATUS[r.status]}{r.field ? `; ${r.field === "desired_start" ? "время" : r.field === "volume_m3" ? "объём" : "марка"}: ${r.field === "desired_start" ? fmtTime(r.old) + " → " + fmtTime(r.new) : `${r.old} → ${r.new}`}` : ""}</div>
              <div className="small">{r.user}{r.reason ? `: ${r.reason}` : ""}, {fmtDateTime(r.at)}</div>
            </div>
          ))}
        </div>
      )}
    </Block>
  );
}

/** ФТ-4.5: отгрузки, которые оператор записал без системы (не было связи), — привязать к рейсу заявки. */
function ManualShipments() {
  const toast = useToast();
  const list = useAsync<any[]>(() => api.get("/trips/manual-shipments"), []);
  useLive(live, ["batch.", "trip."], () => list.reload(), 1000);
  if (!list.data || list.data.length === 0) return null;
  return (
    <Block title={`Отгрузки без системы — привязать к заявке: ${list.data.length}`}>
      <p className="small muted" style={{ marginTop: 0 }}>Оператор отгрузил, когда не было связи. Выберите рейс, которым это было: рейс станет «Выехал», объём и накладная подставятся.</p>
      {list.data.map((b) => (
        <div key={b.id} className="work-card" style={{ marginBottom: 8 }}>
          <div className="row between">
            <b>{fmtDateTime(b.at)} · {b.unit_name} · {b.plate} · {b.grade}, {fmtNum(b.volume_m3)} м³</b>
            {b.comment && <span className="small muted">{b.comment}</span>}
          </div>
          {b.candidates.length === 0 ? <div className="small muted">Подходящих рейсов нет: та же марка, тот же завод, погрузка в пределах 12 часов. Создайте заявку и привяжите позже.</div> : (
            <div className="row tight">{b.candidates.map((c: any) => (
              <Button key={c.trip_id} size="small" onClick={async () => {
                try { await api.post(`/trips/manual-shipments/${b.id}/link`, { trip_id: c.trip_id }); toast.ok(`Привязано к заявке №${c.order_number}, рейс ${c.seq}`); list.reload(); } catch (e) { toast.error(e); }
              }}>№{c.order_number}, рейс {c.seq} · {fmtTime(c.slot_start)}{c.plate ? ` · ${c.plate}` : ""} · {c.customer}</Button>
            ))}</div>
          )}
        </div>
      ))}
    </Block>
  );
}
